/* <brick-chat> — help chat (sections/chat-widget.liquid). Quick replies answer
   instantly from <template>s; the message flow posts to Shopify's native
   contact form handler (/contact) with fetch, so messages land in the store
   inbox. No external server, no AI service. */
(() => {
  if (customElements.get('brick-chat')) return;

  const root = document.documentElement;
  const KEY_SEEN = 'brick-chat:seen';
  const KEY_CONTACT = 'brick-chat:contact';
  const KEY_SENT = 'brick-chat:sent';
  const KEY_PENDING = 'brick-chat:pending';
  const MARKER = '[Sent from the website chat]';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const store = (type) => {
    try {
      const s = window[type];
      const probe = '__chat';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (error) {
      return null;
    }
  };
  const local = store('localStorage');
  const session = store('sessionStorage');
  const get = (s, key) => {
    try {
      return s ? s.getItem(key) : null;
    } catch (error) {
      return null;
    }
  };
  const set = (s, key, value) => {
    try {
      if (s) s.setItem(key, value);
    } catch (error) {
      /* storage full or blocked */
    }
  };
  const remove = (s, key) => {
    try {
      if (s) s.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  };

  const reducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    getComputedStyle(root).getPropertyValue('--motion-scale').trim() === '0';
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const visible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

  class BrickChat extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.launcher = this.querySelector('[data-chat-launcher]');
      this.dialog = this.querySelector('[data-chat-panel]');
      this.log = this.querySelector('[data-chat-log]');
      this.chips = this.querySelector('[data-chat-chips]');
      this.form = this.querySelector('[data-chat-form]');
      this.error = this.querySelector('[data-chat-error]');
      this.message = this.querySelector('[data-chat-message]');
      this.name = this.querySelector('[data-chat-name]');
      this.email = this.querySelector('[data-chat-email]');
      this.honeypot = this.querySelector('[data-chat-hp]');
      this.bodyField = this.querySelector('[data-chat-body]');
      this.submitLabel = this.querySelector('[data-chat-submit-label]');
      if (!this.launcher || !this.dialog) return;

      if (this.hasAttribute('data-hide-mobile') && window.matchMedia('(max-width: 749px)').matches) return;
      this.side = this.dataset.side === 'left' ? 'left' : 'right';
      this.max = Math.max(1, parseInt(this.dataset.max, 10) || 3);
      this.step = 'idle';
      this.greeted = false;

      this.prefillContact();

      this.launcher.addEventListener('click', () => this.open());
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      // Native close (e.g. Escape without a cancel event): keep state in sync.
      this.dialog.addEventListener('close', () => {
        clearTimeout(this.closeTimer);
        this.dialog.classList.remove('is-closing');
        this.launcher.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
      });
      this.dialog.addEventListener('click', (event) => this.onDialogClick(event));
      this.dialog.addEventListener('keydown', (event) => this.trapFocus(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.message.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
          event.preventDefault();
          this.form.requestSubmit ? this.form.requestSubmit() : this.onSubmit(event);
        }
      });
      [this.message, this.name, this.email].forEach((field) =>
        field.addEventListener('input', () => {
          if (field.getAttribute('aria-invalid') === 'true') this.clearError();
        })
      );

      this.watchOverlays();

      // Returning from the native fallback (spam challenge solved).
      const params = new URLSearchParams(window.location.search);
      if (params.get('contact_posted') === 'true' && get(session, KEY_PENDING)) {
        remove(session, KEY_PENDING);
        this.countSent();
        this.reveal();
        this.open({ restored: true });
        return;
      }

      if (this.hasAttribute('data-design-mode')) {
        this.reveal();
        document.addEventListener('shopify:section:select', (event) => {
          if (this.closest('.shopify-section') === event.target) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (this.closest('.shopify-section') === event.target) this.close();
        });
        document.addEventListener('shopify:block:select', (event) => {
          if (this.contains(event.target)) this.open();
        });
        return;
      }

      const delay = Math.max(0, parseFloat(this.dataset.delay) || 0) * 1000;
      this.revealTimer = setTimeout(() => this.reveal(), delay);
    }

    disconnectedCallback() {
      clearTimeout(this.revealTimer);
      if (this.overlayObserver) this.overlayObserver.disconnect();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.bannerObserver) this.bannerObserver.disconnect();
      root.classList.remove('has-brick-chat', 'has-brick-chat--left', 'has-brick-chat--right');
      this.bound = false;
    }

    /* ------------------------------------------------------------------ */
    /* Launcher                                                            */
    /* ------------------------------------------------------------------ */

    reveal() {
      if (!this.launcher.hidden) return;
      this.launcher.hidden = false;
      if (!get(local, KEY_SEEN)) this.classList.add('has-dot');
      root.classList.add('has-brick-chat', `has-brick-chat--${this.side}`);
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-ready')));
    }

    /** Hide the launcher while another modal (cart drawer, menu, lightbox) or the cart toast is up. */
    watchOverlays() {
      const update = () => {
        const otherDialog = Array.from(document.querySelectorAll('dialog[open]')).some((d) => d !== this.dialog);
        const toast = document.querySelector('.cart-toast.is-visible');
        const obscured = otherDialog || !!toast;
        this.classList.toggle('is-obscured', obscured);
        this.launcher.inert = obscured;
      };
      this.overlayObserver = new MutationObserver(update);
      this.overlayObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
      const toast = document.querySelector('.cart-toast');
      if (toast) this.overlayObserver.observe(toast, { attributes: true, attributeFilter: ['class'] });

      // Shopify cookie banner (customer privacy): lift the launcher above it.
      const trackBanner = () => {
        const banner = document.getElementById('shopify-pc__banner');
        if (!banner || banner === this.banner) return;
        this.banner = banner;
        const measure = () => {
          const h = visible(banner) ? Math.round(banner.getBoundingClientRect().height) : 0;
          const fixedBottom = h && window.innerHeight - banner.getBoundingClientRect().bottom < 4;
          this.style.setProperty('--chat-banner-offset', `${fixedBottom ? h : 0}px`);
        };
        measure();
        if ('ResizeObserver' in window) {
          this.bannerObserver = new ResizeObserver(measure);
          this.bannerObserver.observe(banner);
        }
        new MutationObserver(measure).observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      };
      trackBanner();
      this.bodyObserver = new MutationObserver(trackBanner);
      this.bodyObserver.observe(document.body, { childList: true });
      update();
    }

    /* ------------------------------------------------------------------ */
    /* Panel                                                               */
    /* ------------------------------------------------------------------ */

    open({ restored = false } = {}) {
      if (this.dialog.open) return;
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      this.opener = document.activeElement;
      set(local, KEY_SEEN, '1');
      this.classList.remove('has-dot');
      this.dialog.showModal();
      this.launcher.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');

      if (restored) {
        this.greeted = true;
        this.chips.hidden = false;
        this.botFromTemplate('sent', { instant: true });
        this.showAgainIfAllowed();
      } else if (!this.greeted) {
        this.greeted = true;
        this.bot(this.fromTemplate('greeting')).then(() => {
          this.chips.hidden = false;
          this.scrollLog();
        });
      }
      this.focusStart();
    }

    close({ returnFocus = true } = {}) {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      this.launcher.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      const done = () => {
        this.dialog.classList.remove('is-closing');
        if (this.dialog.open) this.dialog.close();
        if (returnFocus) this.launcher.focus({ preventScroll: true });
      };
      if (reducedMotion()) return done();
      this.dialog.classList.add('is-closing');
      this.closeTimer = setTimeout(done, 240);
    }

    focusStart() {
      requestAnimationFrame(() => {
        let target = null;
        if (!this.form.hidden) target = this.step === 'details' ? this.firstEmpty() : this.message;
        if (!target) target = this.dialog.querySelector('[data-chat-close]');
        if (target) target.focus({ preventScroll: true });
      });
    }

    onDialogClick(event) {
      // Backdrop clicks land on the <dialog> itself, outside the sheet.
      if (event.target === this.dialog) {
        const sheet = this.dialog.firstElementChild.getBoundingClientRect();
        const inside =
          event.clientX >= sheet.left && event.clientX <= sheet.right && event.clientY >= sheet.top && event.clientY <= sheet.bottom;
        if (!inside) this.close();
        return;
      }
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.hasAttribute('data-chat-close')) return this.close();
      if (target.hasAttribute('data-chip')) return this.onChip(target);
      if (target.hasAttribute('data-chat-back')) return this.backToTopics();
      if (target.hasAttribute('data-chat-again')) return this.startForm(null, { again: true });
      if (target.hasAttribute('data-chat-continue')) return this.nativeSubmit();
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => visible(el) && !el.closest('[hidden]') && !el.closest('.brick-chat__hp')
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ------------------------------------------------------------------ */
    /* Messages                                                            */
    /* ------------------------------------------------------------------ */

    fromTemplate(name) {
      const tpl = this.querySelector(`template[data-tpl="${CSS.escape(name)}"]`);
      return tpl ? tpl.content.cloneNode(true) : null;
    }

    bubble(who, content) {
      const item = document.createElement('div');
      item.className = `brick-chat__msg brick-chat__msg--${who}`;
      const bubble = document.createElement('div');
      bubble.className = 'brick-chat__bubble';
      if (typeof content === 'string') bubble.textContent = content;
      else if (content) bubble.append(content);
      item.append(bubble);
      this.log.append(item);
      this.scrollLog();
      return item;
    }

    user(text) {
      return this.bubble('user', text);
    }

    showTyping() {
      const typing = document.createElement('div');
      typing.className = 'brick-chat__msg brick-chat__msg--bot brick-chat__msg--typing';
      typing.setAttribute('aria-hidden', 'true');
      typing.innerHTML = '<div class="brick-chat__bubble"><span class="brick-chat__dots"><i></i><i></i><i></i></span></div>';
      this.log.append(typing);
      this.scrollLog();
      return typing;
    }

    /** Typing indicator, then the bot message. */
    async bot(content, { instant = false } = {}) {
      if (!content) return null;
      const token = (this.botToken = (this.botToken || 0) + 1);
      if (!instant && !reducedMotion()) {
        const typing = this.showTyping();
        await wait(650);
        typing.remove();
        if (token !== this.botToken) return null;
      }
      return this.bubble('bot', content);
    }

    botFromTemplate(name, options) {
      return this.bot(this.fromTemplate(name), options);
    }

    scrollLog() {
      this.log.scrollTo({ top: this.log.scrollHeight, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /* ------------------------------------------------------------------ */
    /* Quick replies                                                       */
    /* ------------------------------------------------------------------ */

    async onChip(chip) {
      const action = chip.dataset.action;
      this.chips.querySelectorAll('[aria-pressed]').forEach((el) => el.removeAttribute('aria-pressed'));
      chip.setAttribute('aria-pressed', 'true');
      this.user(chip.textContent.trim());

      if (action === 'form') return this.startForm(chip.dataset.chip);

      await this.bot(this.fromTemplate(chip.dataset.chip));
    }

    /* ------------------------------------------------------------------ */
    /* Message flow                                                        */
    /* ------------------------------------------------------------------ */

    sentCount() {
      return parseInt(get(session, KEY_SENT), 10) || 0;
    }

    countSent() {
      set(session, KEY_SENT, String(this.sentCount() + 1));
    }

    async startForm(chipId, { again = false } = {}) {
      if (this.sentCount() >= this.max) {
        this.form.hidden = true;
        this.botFromTemplate('limit');
        return;
      }
      if (again) this.log.querySelectorAll('[data-chat-again]').forEach((btn) => btn.closest('.brick-chat__msg').remove());
      const prompt = chipId ? this.fromTemplate(chipId) : null;
      const hasPrompt = prompt && prompt.textContent.trim();
      await this.bot(hasPrompt ? prompt : this.fromTemplate('ask-message'));
      this.chips.hidden = true;
      this.setStep('message');
      this.form.hidden = false;
      this.message.value = '';
      this.scrollLog();
      this.message.focus({ preventScroll: true });
    }

    backToTopics() {
      this.form.hidden = true;
      this.clearError();
      this.setStep('idle');
      this.chips.hidden = false;
      const first = this.chips.querySelector('button');
      if (first) first.focus();
    }

    setStep(step) {
      this.step = step;
      this.form.querySelectorAll('[data-step]').forEach((el) => {
        el.hidden = el.dataset.step !== step;
      });
      this.form.dataset.step = step;
    }

    firstEmpty() {
      if (!this.name.value.trim()) return this.name;
      if (!EMAIL_RE.test(this.email.value.trim())) return this.email;
      return this.form.querySelector('[data-chat-submit]');
    }

    showError(field, text) {
      this.error.textContent = text;
      this.error.hidden = false;
      [this.message, this.name, this.email].forEach((el) => {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', this.error.id);
        field.focus();
      }
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      [this.message, this.name, this.email].forEach((el) => {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.clearError();

      if (this.step === 'message') {
        const text = this.message.value.trim();
        if (text.length < 2) return this.showError(this.message, this.dataset.textErrorMessage);
        this.pendingMessage = text;
        this.user(text);
        this.setStep('busy');
        await this.botFromTemplate('ask-details');
        this.setStep('details');
        this.scrollLog();
        this.firstEmpty().focus({ preventScroll: true });
        return;
      }

      if (this.step !== 'details') return;
      const name = this.name.value.trim();
      const email = this.email.value.trim();
      if (!name) return this.showError(this.name, this.dataset.textErrorName);
      if (!EMAIL_RE.test(email)) return this.showError(this.email, this.dataset.textErrorEmail);
      if (this.sentCount() >= this.max) {
        this.form.hidden = true;
        return this.botFromTemplate('limit');
      }

      set(local, KEY_CONTACT, JSON.stringify({ name, email }));
      this.user(`${name} · ${email}`);
      this.send(name, email);
    }

    prefillContact() {
      try {
        const saved = JSON.parse(get(local, KEY_CONTACT) || 'null');
        if (saved && typeof saved === 'object') {
          if (!this.name.value && saved.name) this.name.value = String(saved.name).slice(0, 120);
          if (!this.email.value && saved.email) this.email.value = String(saved.email).slice(0, 254);
        }
      } catch (error) {
        /* ignore malformed storage */
      }
    }

    async cartCount() {
      const fallback = parseInt(this.dataset.cartCount, 10) || 0;
      if (!this.dataset.cartUrl || !window.fetch) return fallback;
      const controller = 'AbortController' in window ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 2000) : null;
      try {
        const res = await fetch(this.dataset.cartUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller ? controller.signal : undefined,
        });
        const cart = await res.json();
        return typeof cart.item_count === 'number' ? cart.item_count : fallback;
      } catch (error) {
        return fallback;
      } finally {
        clearTimeout(timer);
      }
    }

    async composeBody() {
      const lines = [
        this.pendingMessage || '',
        '',
        '---',
        MARKER,
        `Page: ${window.location.href}`,
        `Page title: ${document.title}`,
      ];
      if (this.dataset.productTitle) {
        lines.push(`Product: ${this.dataset.productTitle} (${this.dataset.productHandle || ''})`);
      }
      lines.push(`Cart items: ${await this.cartCount()}`);
      lines.push(`Locale: ${this.dataset.locale || root.lang || ''}`);
      return lines.join('\n');
    }

    /** Builds the request exactly like the native contact form. */
    async buildPayload(name, email) {
      const data = new FormData();
      data.append('form_type', 'contact');
      data.append('utf8', '✓');
      data.append('contact[name]', name);
      data.append('contact[email]', email);
      data.append('contact[body]', await this.composeBody());
      data.append('contact[Source]', 'Website chat');
      return data;
    }

    async send(name, email) {
      this.busy = true;
      this.setStep('busy');
      this.form.setAttribute('aria-busy', 'true');

      // Honeypot filled: act as if sent, send nothing.
      if (this.honeypot && this.honeypot.value) {
        await wait(600);
        return this.finish('sent');
      }

      const data = await this.buildPayload(name, email);
      if (this.bodyField) this.bodyField.value = data.get('contact[body]');

      const typing = this.showTyping();
      if (this.submitLabel) this.form.setAttribute('aria-label', this.dataset.textSending || '');

      let result = 'failed';
      try {
        const res = await fetch(this.form.dataset.endpoint, {
          method: 'POST',
          body: new URLSearchParams(data),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        result = await this.readResult(res);
      } catch (error) {
        result = 'failed';
      }
      typing.remove();
      this.form.removeAttribute('aria-label');
      this.finish(result);
    }

    async readResult(res) {
      const url = new URL(res.url || this.form.dataset.endpoint, window.location.href);
      if (/\/challenge\b/.test(url.pathname)) return 'challenge';
      if (url.searchParams.get('contact_posted') === 'true') return 'sent';
      if (!res.ok) return 'failed';
      const html = await res.text().catch(() => '');
      if (/contact_posted=true|posted_successfully/i.test(html)) return 'sent';
      if (/h-captcha|g-recaptcha|\/challenge/i.test(html)) return 'challenge';
      return 'failed';
    }

    async finish(result) {
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      if (result === 'sent') {
        this.countSent();
        this.form.hidden = true;
        this.setStep('idle');
        this.message.value = '';
        this.pendingMessage = '';
        const sent = await this.botFromTemplate('sent');
        this.chips.hidden = false;
        const again = this.showAgainIfAllowed();
        this.focusIn(again || sent);
      } else {
        this.form.hidden = true;
        this.setStep('idle');
        const msg = await this.botFromTemplate(result === 'challenge' ? 'challenge' : 'failed');
        this.focusIn(msg);
      }
      this.scrollLog();
    }

    showAgainIfAllowed() {
      if (this.sentCount() >= this.max) return;
      const again = this.fromTemplate('again');
      return again ? this.bubble('bot brick-chat__msg--action', again) : null;
    }

    /** Keep focus inside the dialog after the composer disappears. */
    focusIn(container) {
      const target =
        (container && container.querySelector('a, button')) || this.chips.querySelector('button') || this.dialog.querySelector('[data-chat-close]');
      if (target && this.dialog.open) target.focus({ preventScroll: true });
    }

    /** Spam challenge fallback: a real form POST lets the visitor solve the captcha. */
    nativeSubmit() {
      if (!this.bodyField || !this.bodyField.value) return;
      set(session, KEY_PENDING, '1');
      [
        ['form_type', 'contact'],
        ['utf8', '✓'],
      ].forEach(([name, value]) => {
        let input = this.form.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          this.form.append(input);
        }
        input.value = value;
      });
      this.form.method = 'post';
      this.form.action = this.form.dataset.endpoint;
      HTMLFormElement.prototype.submit.call(this.form);
    }
  }

  customElements.define('brick-chat', BrickChat);
})();
